import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { demoAPI } from '@/lib/api';
import { Eye, ArrowLeft, Download, Loader2, ChevronDown, AlertTriangle, Copy } from 'lucide-react';
import { toast } from 'sonner';

function SeverityBadge({ severity }) {
  const s = severity?.toLowerCase();
  if (s === 'high') return <span className="bg-rose-100 text-rose-700 border border-rose-200 px-2 py-0.5 text-xs font-bold uppercase tracking-wider">High</span>;
  if (s === 'medium') return <span className="bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 text-xs font-bold uppercase tracking-wider">Medium</span>;
  return <span className="bg-emerald-100 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-xs font-bold uppercase tracking-wider">Low</span>;
}

function RiskScoreDisplay({ score }) {
  let color, label;
  if (score >= 7) { color = 'text-rose-600'; label = 'High Risk'; }
  else if (score >= 4) { color = 'text-amber-600'; label = 'Moderate Risk'; }
  else { color = 'text-emerald-600'; label = 'Low Risk'; }
  return (
    <div className="text-center" data-testid="demo-risk-score">
      <div className={`font-mono font-bold text-6xl ${color}`}>{score}</div>
      <div className="font-mono text-sm text-zinc-400 mt-1">/ 10</div>
      <div className={`font-body text-sm font-bold mt-2 ${color}`}>{label}</div>
    </div>
  );
}

function CollapsibleSection({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-zinc-200 bg-white">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-6 py-4 hover:bg-zinc-50 transition-colors">
        <span className="font-heading font-bold text-sm text-zinc-900">{title}</span>
        <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="px-6 pb-6 border-t border-zinc-100">{children}</div>}
    </div>
  );
}

export default function DemoPage() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const res = await demoAPI.getAnalysis();
        setReport(res.data.report);
      } catch {
        toast.error('Failed to load demo');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const clauseLabels = {
    payment_terms: 'Payment Terms', termination: 'Termination', liability: 'Liability',
    indemnity: 'Indemnity', confidentiality: 'Confidentiality', exclusivity: 'Exclusivity',
    renewal: 'Renewal', governing_law: 'Governing Law', dispute_resolution: 'Dispute Resolution',
    ip_ownership: 'IP Ownership', non_compete: 'Non-Compete',
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-brand animate-spin" />
      </div>
    );
  }

  if (!report) return null;
  const clauses = report.extracted_clauses || {};

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="demo-page">
      {/* Navbar */}
      <header className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-md border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <Eye className="w-6 h-6 text-brand" strokeWidth={2} />
            <span className="font-heading font-black text-lg tracking-tight text-zinc-900">ContractEye</span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="bg-amber-100 text-amber-700 border border-amber-200 px-3 py-1 text-xs font-bold tracking-wider uppercase font-body">Demo</span>
            <button onClick={() => navigate('/signup')} className="bg-brand text-white hover:bg-brand-hover px-5 py-2 font-body font-medium text-sm transition-colors" data-testid="demo-signup-btn">Sign Up Free</button>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <Link to="/" className="flex items-center gap-1 font-body text-sm text-zinc-500 hover:text-zinc-700 mb-6" data-testid="demo-back-home">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>

          {/* Demo Banner */}
          <div className="bg-blue-50 border border-blue-200 px-4 py-3 mb-6" data-testid="demo-banner">
            <p className="font-body text-sm text-blue-800">
              This is a <strong>demo analysis</strong> of a sample freelance consulting agreement.{' '}
              <button onClick={() => navigate('/signup')} className="underline font-medium">Sign up</button> to analyze your own contracts.
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 px-4 py-3 mb-6">
            <p className="font-body text-xs text-amber-800">
              <strong>Disclaimer:</strong> This analysis provides risk insights only and does not constitute legal advice.
            </p>
          </div>

          <div className="grid lg:grid-cols-12 gap-6">
            <div className="lg:col-span-8 space-y-4">
              <div className="bg-white border border-zinc-200 p-6">
                <p className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 font-body mb-1">Demo Report</p>
                <h1 className="font-heading font-bold text-xl tracking-tight text-zinc-900">{report.file_name}</h1>
              </div>

              <CollapsibleSection title="Executive Summary">
                <p className="font-body text-sm text-zinc-700 leading-relaxed whitespace-pre-wrap mt-2" data-testid="demo-summary">{report.executive_summary}</p>
              </CollapsibleSection>

              <CollapsibleSection title="Key Obligations">
                <ul className="space-y-2 mt-2">
                  {(report.key_obligations || []).map((o, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="font-mono text-xs text-zinc-400 mt-0.5 shrink-0">{String(i + 1).padStart(2, '0')}</span>
                      <span className="font-body text-sm text-zinc-700">{o}</span>
                    </li>
                  ))}
                </ul>
              </CollapsibleSection>

              <CollapsibleSection title={`Risk Flags (${(report.risk_flags || []).length})`}>
                <div className="space-y-3 mt-2">
                  {(report.risk_flags || []).map((f, i) => (
                    <div key={i} className="border border-zinc-100 p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <SeverityBadge severity={f.severity} />
                        <span className="font-heading font-bold text-sm text-zinc-900">{f.category}</span>
                      </div>
                      {f.clause_excerpt && <p className="font-mono text-xs text-zinc-500 bg-zinc-50 p-2 mb-2 border-l-2 border-zinc-200 italic">"{f.clause_excerpt}"</p>}
                      <p className="font-body text-sm text-zinc-700">{f.concern}</p>
                    </div>
                  ))}
                </div>
              </CollapsibleSection>

              <CollapsibleSection title="Suggested Revisions">
                <ul className="space-y-2 mt-2">
                  {(report.suggested_revisions || []).map((r, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="font-mono text-xs text-zinc-400 mt-0.5 shrink-0">{String(i + 1).padStart(2, '0')}</span>
                      <span className="font-body text-sm text-zinc-700">{r}</span>
                    </li>
                  ))}
                </ul>
              </CollapsibleSection>

              <CollapsibleSection title="Extracted Clauses" defaultOpen={false}>
                <div className="grid md:grid-cols-2 gap-3 mt-2">
                  {Object.entries(clauseLabels).map(([key, label]) => {
                    const val = clauses[key];
                    if (!val) return null;
                    return (
                      <div key={key} className="border border-zinc-100 p-3">
                        <span className="font-body text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1">{label}</span>
                        <p className="font-body text-xs text-zinc-700 leading-relaxed">{val}</p>
                      </div>
                    );
                  })}
                </div>
              </CollapsibleSection>

              <CollapsibleSection title="What to Review with a Lawyer">
                <div className="bg-blue-50 border border-blue-200 p-4 mt-2">
                  <ul className="space-y-2">
                    {(report.lawyer_review_points || []).map((p, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="font-mono text-xs text-blue-400 mt-0.5 shrink-0">{i + 1}.</span>
                        <span className="font-body text-xs text-blue-800">{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </CollapsibleSection>

              {/* CTA */}
              <div className="bg-brand p-8 text-center" data-testid="demo-cta">
                <h3 className="font-heading font-bold text-xl text-white mb-2">Ready to scan your own contracts?</h3>
                <p className="font-body text-sm text-white/80 mb-6">Sign up for free and get 2 contract scans per month.</p>
                <button onClick={() => navigate('/signup')} className="bg-white text-brand hover:bg-zinc-100 px-8 py-3 font-body font-medium text-sm transition-colors" data-testid="demo-cta-signup-btn">
                  Get Started Free
                </button>
              </div>
            </div>

            <div className="lg:col-span-4 space-y-4">
              <div className="bg-white border border-zinc-200 p-6 lg:sticky lg:top-24">
                <p className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 font-body mb-4">Risk Assessment</p>
                <RiskScoreDisplay score={report.risk_score} />
                <div className="mt-6 pt-6 border-t border-zinc-100">
                  <p className="font-body text-xs font-medium text-zinc-700 mb-3">Risk Distribution</p>
                  <div className="space-y-2">
                    {['High', 'Medium', 'Low'].map(s => {
                      const count = (report.risk_flags || []).filter(f => f.severity === s).length;
                      return (
                        <div key={s} className="flex items-center justify-between">
                          <SeverityBadge severity={s} />
                          <span className="font-mono text-xs text-zinc-600">{count} flag{count !== 1 ? 's' : ''}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="mt-6 pt-6 border-t border-zinc-100">
                  <p className="font-body text-xs font-medium text-zinc-700 mb-2">Clauses Found</p>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(clauseLabels).map(([key, label]) => (
                      <span key={key} className={`px-2 py-0.5 text-xs font-body ${clauses[key] ? 'bg-zinc-100 text-zinc-700' : 'bg-zinc-50 text-zinc-300 line-through'}`}>{label}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
